/**
 * price-range-slider.js  —  Planta Greenhouses
 * ---------------------------------------------------------------------------
 * Progressive enhancement for the Dawn/Craft <price-range> collection filter.
 * Adds a dual-handle slider above the existing "From / To" number inputs and
 * keeps the two in sync. The number inputs stay fully editable.
 *
 * Behaviour
 *   - Labels + fill update live while dragging.
 *   - The AJAX product-grid refresh fires once, on handle release.
 *   - Re-initialises itself after Dawn re-renders the facets over AJAX.
 *   - Runs for both the desktop sidebar and the mobile filter drawer.
 *
 * Install: assets/price-range-slider.js, then in layout/theme.liquid before
 * </body>:  <script src="{{ 'price-range-slider.js' | asset_url }}" defer></script>
 *
 * No theme files are modified. Deleting this file fully reverts the change.
 */
(function () {
  'use strict';

  var FLAG = 'prSliderReady';
  var STYLE_ID = 'pr-slider-styles';

  /* ---------------------------------------------------------------- styles */

  var CSS = [
    '.pr-slider{--pr-accent:rgb(var(--color-base-accent-1,26 26 26));',
    '--pr-track:rgba(var(--color-base-text,18 18 18),.16);',
    '--pr-surface:rgb(var(--color-base-background-1,255 255 255));',
    'position:relative;height:1.75rem;margin:.4rem .7rem .3rem;touch-action:pan-y}',

    '.pr-slider__track{position:absolute;inset-inline:0;top:50%;height:4px;',
    'transform:translateY(-50%);border-radius:999px;background:var(--pr-track)}',

    '.pr-slider__fill{position:absolute;top:0;bottom:0;border-radius:999px;',
    'background:var(--pr-accent)}',

    /* both native inputs are stacked, transparent, and click-through except
       for their thumbs, which is what makes the dual handle work */
    '.pr-slider__input{position:absolute;inset-inline:0;top:0;width:100%;',
    'height:1.75rem;margin:0;padding:0;background:none;border:0;',
    'pointer-events:none;-webkit-appearance:none;appearance:none}',
    '.pr-slider__input:focus{outline:0}',

    '.pr-slider__input::-webkit-slider-runnable-track{height:1.75rem;background:none;border:0}',
    '.pr-slider__input::-moz-range-track{height:1.75rem;background:none;border:0}',

    '.pr-slider__input::-webkit-slider-thumb{-webkit-appearance:none;appearance:none;',
    'pointer-events:auto;width:1.15rem;height:1.15rem;margin-top:.3rem;border-radius:50%;',
    'background:var(--pr-surface);border:2px solid var(--pr-accent);',
    'box-shadow:0 1px 4px rgba(0,0,0,.22);cursor:grab;',
    'transition:transform .12s ease,box-shadow .12s ease}',
    '.pr-slider__input::-moz-range-thumb{pointer-events:auto;width:1.15rem;height:1.15rem;',
    'border-radius:50%;background:var(--pr-surface);border:2px solid var(--pr-accent);',
    'box-shadow:0 1px 4px rgba(0,0,0,.22);cursor:grab;',
    'transition:transform .12s ease,box-shadow .12s ease}',

    '.pr-slider__input:hover::-webkit-slider-thumb{transform:scale(1.12)}',
    '.pr-slider__input:hover::-moz-range-thumb{transform:scale(1.12)}',
    '.pr-slider__input:active::-webkit-slider-thumb{cursor:grabbing;transform:scale(1.12)}',
    '.pr-slider__input:active::-moz-range-thumb{cursor:grabbing;transform:scale(1.12)}',

    '.pr-slider__input:focus-visible::-webkit-slider-thumb{',
    'box-shadow:0 0 0 3px var(--pr-surface),0 0 0 5px var(--pr-accent)}',
    '.pr-slider__input:focus-visible::-moz-range-thumb{',
    'box-shadow:0 0 0 3px var(--pr-surface),0 0 0 5px var(--pr-accent)}',

    '@media (prefers-reduced-motion:reduce){',
    '.pr-slider__input::-webkit-slider-thumb,.pr-slider__input::-moz-range-thumb{transition:none}}'
  ].join('');

  function injectStyles() {
    if (document.getElementById(STYLE_ID)) return;
    var el = document.createElement('style');
    el.id = STYLE_ID;
    el.textContent = CSS;
    document.head.appendChild(el);
  }

  /* ----------------------------------------------------------------- utils */

  function num(value) {
    var n = parseFloat(String(value).replace(/[^0-9.\-]/g, ''));
    return isNaN(n) ? null : n;
  }

  function clamp(n, lo, hi) {
    return Math.min(hi, Math.max(lo, n));
  }

  /** Absolute bounds live on the placeholders — Dawn rewrites min/max as you filter. */
  function bounds(minInput, maxInput) {
    var lo = num(minInput.placeholder);
    var hi = num(maxInput.placeholder);
    if (lo === null) lo = num(minInput.getAttribute('min')) || 0;
    if (hi === null) hi = num(maxInput.getAttribute('max'));
    return { lo: lo || 0, hi: hi };
  }

  function stepFor(range) {
    if (range <= 50) return 1;
    if (range <= 200) return 5;
    if (range <= 1000) return 10;
    if (range <= 5000) return 25;
    return 50;
  }

  var money = (function () {
    var code = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'USD';
    try {
      var fmt = new Intl.NumberFormat(document.documentElement.lang || 'en', {
        style: 'currency', currency: code, minimumFractionDigits: 0, maximumFractionDigits: 0
      });
      return function (n) { return fmt.format(n); };
    } catch (e) {
      return function (n) { return '$' + Math.round(n).toLocaleString(); };
    }
  })();

  /* ------------------------------------------------------------------ core */

  function enhance(host) {
    if (host.dataset[FLAG]) return;

    var inputs = host.querySelectorAll('input[type="number"], input[name^="filter.v.price"]');
    if (inputs.length < 2) return;

    var minInput = inputs[0];
    var maxInput = inputs[1];
    var b = bounds(minInput, maxInput);
    if (b.hi === null || b.hi <= b.lo) return; // nothing sensible to slide over

    host.dataset[FLAG] = '1';

    var step = stepFor(b.hi - b.lo);
    var busy = false; // guards against our own dispatches re-entering the sync

    var wrap = document.createElement('div');
    wrap.className = 'pr-slider';
    wrap.innerHTML =
      '<div class="pr-slider__track"><div class="pr-slider__fill"></div></div>' +
      '<input type="range" class="pr-slider__input pr-slider__input--min">' +
      '<input type="range" class="pr-slider__input pr-slider__input--max">';

    var fill = wrap.querySelector('.pr-slider__fill');
    var lower = wrap.querySelector('.pr-slider__input--min');
    var upper = wrap.querySelector('.pr-slider__input--max');

    [lower, upper].forEach(function (r) {
      r.min = b.lo;
      r.max = b.hi;
      r.step = step;
    });
    lower.setAttribute('aria-label', minInput.getAttribute('aria-label') || 'Minimum price');
    upper.setAttribute('aria-label', maxInput.getAttribute('aria-label') || 'Maximum price');

    // sits above <price-range>, not inside it, so Dawn's From/To flex row is untouched
    host.parentNode.insertBefore(wrap, host);

    /** Paint track fill, handle positions and the accessible value text. */
    function paint(lo, hi) {
      var span = b.hi - b.lo || 1;
      var a = ((lo - b.lo) / span) * 100;
      var z = ((hi - b.lo) / span) * 100;
      fill.style.insetInlineStart = a + '%';
      fill.style.insetInlineEnd = (100 - z) + '%';
      lower.setAttribute('aria-valuetext', money(lo));
      upper.setAttribute('aria-valuetext', money(hi));

      // whichever handle still has somewhere to go wins the click target
      if (Math.abs(hi - lo) <= step) {
        lower.style.zIndex = hi >= b.hi ? 5 : 3;
        upper.style.zIndex = hi >= b.hi ? 3 : 5;
      } else {
        lower.style.zIndex = 3;
        upper.style.zIndex = 4;
      }
    }

    /** Read the authoritative values out of Dawn's number fields. */
    function readFields() {
      var lo = num(minInput.value);
      var hi = num(maxInput.value);
      return {
        lo: lo === null ? b.lo : clamp(lo, b.lo, b.hi),
        hi: hi === null ? b.hi : clamp(hi, b.lo, b.hi)
      };
    }

    function syncFromFields() {
      if (busy) return;
      var v = readFields();
      lower.value = v.lo;
      upper.value = v.hi;
      // read back: the browser snaps each value to the nearest step, and the
      // fill should line up with the handles rather than the typed number
      paint(parseFloat(lower.value), parseFloat(upper.value));
    }

    /** Write slider values back into the number fields (display only). */
    function writeFields(lo, hi) {
      minInput.value = lo > b.lo ? lo : '';
      maxInput.value = hi < b.hi ? hi : '';
    }

    /**
     * Hand control back to Dawn so it refetches the product grid.
     *
     * Deliberately does NOT dispatch 'change' on the number inputs.
     * Dawn's PriceRange.adjustToValidValues() reads an empty box as
     * Number('') === 0, so firing 'change' at an untouched, empty "To" field
     * just after "From" gained a value makes Dawn clamp "To" up to the new
     * minimum — the range collapses to a single point (gte=2500&lte=2500) and
     * the grid comes back empty. Stock Dawn never trips over this because a
     * real user only ever fires 'change' on the one box they actually edited.
     *
     * So we do Dawn's setMinAndMaxValues() bookkeeping here ourselves —
     * attributes only, never values — and fire the single bubbling 'input'
     * event that FacetFiltersForm is actually listening for.
     */
    function commit(changed) {
      busy = true;
      minInput.setAttribute('max', maxInput.value || b.hi);
      maxInput.setAttribute('min', minInput.value || b.lo);
      changed.dispatchEvent(new Event('input', { bubbles: true }));
      busy = false;
    }

    function onDrag() {
      var lo = parseFloat(lower.value);
      var hi = parseFloat(upper.value);
      if (this === lower && lo > hi) { hi = lo; upper.value = hi; }
      if (this === upper && hi < lo) { lo = hi; lower.value = lo; }
      writeFields(lo, hi);
      paint(lo, hi);
    }

    lower.addEventListener('input', onDrag);
    upper.addEventListener('input', onDrag);
    // 'change' on a range input fires on release — one refetch per drag
    lower.addEventListener('change', function () { commit(minInput); });
    upper.addEventListener('change', function () { commit(maxInput); });

    // typing in the From/To boxes moves the handles
    minInput.addEventListener('input', syncFromFields);
    maxInput.addEventListener('input', syncFromFields);
    minInput.addEventListener('change', syncFromFields);
    maxInput.addEventListener('change', syncFromFields);

    syncFromFields();
  }

  /* ------------------------------------------------------------- lifecycle */

  function scan(root) {
    (root || document).querySelectorAll('price-range').forEach(enhance);
  }

  function start() {
    injectStyles();
    scan();

    // Dawn swaps facet markup wholesale after every AJAX filter — re-enhance.
    new MutationObserver(function (records) {
      for (var i = 0; i < records.length; i++) {
        var added = records[i].addedNodes;
        for (var j = 0; j < added.length; j++) {
          var node = added[j];
          if (node.nodeType !== 1) continue;
          if (node.tagName === 'PRICE-RANGE') enhance(node);
          else if (node.querySelector) scan(node);
        }
      }
    }).observe(document.documentElement, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();